/**
 * A starter in the row, drawn as the production canvas card: a poster, the
 * title under it and one line on what the canvas is about. Nothing marks it as
 * a starter; the click does.
 */

import { cn } from "@/utils";
import {
  gridCardFooterSubtitleClassName,
  gridCardFooterTitleClassName,
  WorkspaceCardFooterTitleRow,
  WorkspaceCardFooterTypeIcon,
  WorkspaceGridCard,
} from "@cohesive/ui-components";
import { Frame, type LucideIcon } from "lucide-react";
import type { ReactNode } from "react";

export interface StarterCardProps {
  title: string;
  subtitle: string;
  preview: ReactNode;
  onOpen: () => void;
  /** The title row's glyph; the canvas frame unless the starter is not one. */
  icon?: LucideIcon;
  /** The starter is turning into a canvas; the card takes no second click. */
  busy?: boolean;
}

export function StarterCard({
  title,
  subtitle,
  preview,
  onOpen,
  icon = Frame,
  busy = false,
}: StarterCardProps) {
  return (
    <WorkspaceGridCard
      interactive={!busy}
      aria-label={`Open ${title}`}
      aria-busy={busy || undefined}
      data-starter=""
      onClick={busy ? undefined : onOpen}
      // Tighter than the production card: 12px to the title, 4px under it.
      className={cn("gap-3", busy && "pointer-events-none")}
      preview={preview}
      footer={
        <div className="min-w-0 flex-1">
          <WorkspaceCardFooterTitleRow
            icon={<WorkspaceCardFooterTypeIcon icon={icon} />}
          >
            <p className={gridCardFooterTitleClassName}>{title}</p>
          </WorkspaceCardFooterTitleRow>
          <p className={cn("mt-1 truncate", gridCardFooterSubtitleClassName)}>
            {subtitle}
          </p>
        </div>
      }
    />
  );
}
